import { motion, useReducedMotion } from "framer-motion";
import { Check, CalendarPlus, Download } from "lucide-react";
import { fmtDateLong, fmtTime, isAbroad, localTzLabel, TZ, userTz } from "@/lib/format";

const compact = (iso) => iso.replace(/[-:]/g, "");
const TITLE = "FAD — Dubai Real Estate Briefing";
const DETAILS = "Your Dubai real estate briefing with FAD (Firaol Amsalu Dessalegn). Meeting details will be shared before the briefing.";

function googleUrl(r) {
  const p = new URLSearchParams({ action: "TEMPLATE", text: TITLE, dates: `${compact(r.start_utc)}/${compact(r.end_utc)}`, details: DETAILS });
  return `https://calendar.google.com/calendar/render?${p.toString()}`;
}

function downloadIcs(r) {
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//FAD//Briefing//EN", "BEGIN:VEVENT", `UID:${r.id}@fad`, `DTSTAMP:${compact(new Date().toISOString().slice(0, 19) + "Z")}`, `DTSTART:${compact(r.start_utc)}`, `DTEND:${compact(r.end_utc)}`, `SUMMARY:${TITLE}`, `DESCRIPTION:${DETAILS}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "fad-briefing.ics";
  a.click();
  URL.revokeObjectURL(url);
}

export function Confirmation({ result, onReset }) {
  const reduce = useReducedMotion();
  const s = result.start_utc;
  const rows = [["Date", fmtDateLong(s, userTz), "confirm-date"], ["Time", fmtTime(s, userTz), "confirm-time"], ["Time zone", localTzLabel(s), "confirm-timezone"], ["Duration", `${result.duration} minutes`, "confirm-duration"]];
  if (isAbroad) rows.push(["Dubai time", `${fmtTime(s, TZ)}, ${fmtDateLong(s, TZ)} (GST, UTC+4)`, "confirm-dubai-time"]);
  return (
    <motion.div initial={reduce ? false : { opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl mx-auto text-center py-8 sm:py-12" data-testid="booking-confirmation" aria-live="polite">
      <div className="mx-auto h-16 w-16 rounded-full bg-[#0B1F3A] grid place-items-center shadow-xl">
        <Check size={28} className="text-[#C7A76C]" strokeWidth={2.5} />
      </div>
      <p className="mt-6 eyebrow" data-testid="confirm-status">Booking confirmed</p>
      <h3 className="font-display mt-3 text-3xl sm:text-4xl font-bold text-[#0B1F3A]">You’re booked, {result.full_name.split(" ")[0]}.</h3>
      <dl className="mt-10 grid sm:grid-cols-2 gap-px bg-[rgba(20,35,55,0.10)] rounded-3xl overflow-hidden border border-[rgba(20,35,55,0.10)] text-left">
        {rows.map(([k, v, tid]) => (
          <div key={k} className="bg-white p-5">
            <dt className="text-xs text-[#697386]">{k}</dt>
            <dd className="mt-1 font-semibold text-[#172033]" data-testid={tid}>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-8 text-[15px] text-[#697386] leading-relaxed max-w-lg mx-auto" data-testid="confirm-next-steps">
        A confirmation has been sent to <span className="text-[#172033] font-medium">{result.email}</span>. FAD will reach out on WhatsApp or email before your briefing to share the meeting details. Bring any questions you have.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <a href={googleUrl(result)} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="add-google-calendar-button"><CalendarPlus size={16} /> Add to Google Calendar</a>
        <button onClick={() => downloadIcs(result)} className="inline-flex items-center justify-center gap-2 rounded-full border border-[rgba(20,35,55,0.15)] bg-white px-6 py-3 text-sm font-semibold text-[#0B1F3A] hover:bg-[#F5F7FA] transition-colors" data-testid="download-ics-button"><Download size={16} /> Apple / Outlook (.ics)</button>
      </div>
      <button onClick={onReset} className="mt-8 text-sm text-[#697386] hover:text-[#0B1F3A] underline-offset-4 hover:underline" data-testid="booking-done-button">Done</button>
    </motion.div>
  );
}
